import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { DialogBase, DialogCloseReason } from '../dialogs/base-dialog';
import { CRMAttribute, CRMEntity, PersonalizationEntity } from './crm-attribute';
import { TranslateService } from '@ngx-translate/core';
import { PersonalizationComponentApi } from './personalization.api';
import { Logger } from '../monitoring/logger/logger';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'personalization-dialog',
  templateUrl: './personalization-dialog.html'
})
export class PersonalizationDialogComponent extends DialogBase implements OnInit {

  private contactAttributes: CRMAttribute[];
  private leadAttributes: CRMAttribute[];
  private accountAttributes: CRMAttribute[];
  private ownerAttributes: CRMAttribute[];
  private emailSendAttributes: CRMAttribute[];
  private noneText: string;

  public isLoading: boolean = true;
  public personalizationType: CRMEntity = CRMEntity.Contact;
  public personalizationTypes: PersonalizationEntity[] = [];
  public personalizationAttribute: CRMAttribute;
  public childAttribute: CRMAttribute;
  public selectedAttributes: CRMAttribute[];
  public selectedChildAttributes: CRMAttribute[];
  public showChildAttributes: boolean = false;

  constructor(private personalizationComponentApi: PersonalizationComponentApi,
              private logger: Logger,
              private translate: TranslateService) {
    super();

    this.noneText = translate.instant('COMMON.DIALOGS.PERSONALIZATION.NONE');

    this.personalizationTypes = [
      { name: translate.instant('COMMON.DIALOGS.PERSONALIZATION.PERSONALIZATION_TYPES_CONTACT'), entity: CRMEntity.Contact },
      { name: translate.instant('COMMON.DIALOGS.PERSONALIZATION.PERSONALIZATION_TYPES_LEAD'), entity: CRMEntity.Lead },
      { name: translate.instant('COMMON.DIALOGS.PERSONALIZATION.PERSONALIZATION_TYPES_ACCOUNT'), entity: CRMEntity.Account },
      { name: translate.instant('COMMON.DIALOGS.PERSONALIZATION.PERSONALIZATION_TYPES_OWNER'), entity: CRMEntity.Owner },
      { name: translate.instant('COMMON.DIALOGS.PERSONALIZATION.PERSONALIZATION_TYPES_EMAIL_SEND'), entity: CRMEntity.EmailSend }
    ];
  }

  private setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  public close() {
    super.close({ reason: DialogCloseReason.CANCEL });
  }

  public addPersonalization() {
    const freemarker = (this.showChildAttributes) ? this.childAttribute.freemarker : this.personalizationAttribute.freemarker;
    super.close({ reason: DialogCloseReason.OK, data: freemarker });
  }

  public refresh() {
    this.showChildAttributes = false;
    this.personalizationType = CRMEntity.Contact;
    this.setLoading(true);
    this.getPersonalizationAttributesFromServer();
  }

  private getPersonalizationAttributes(): Observable<any> {
    return this.personalizationComponentApi.getPersonalizationAttributes();
  }

  public setPersonalizationAttributes(): void {
    switch (Number(this.personalizationType)) {
      case CRMEntity.Contact:
        this.selectedAttributes = this.contactAttributes;
        break;
      case CRMEntity.Lead:
        this.selectedAttributes = this.leadAttributes;
        break;
      case CRMEntity.Account:
        this.selectedAttributes = this.accountAttributes;
        break;
      case CRMEntity.Owner:
        this.selectedAttributes = this.ownerAttributes;
        break;
      case CRMEntity.EmailSend:
        this.selectedAttributes = this.emailSendAttributes;
        break;
    }

    if (this.selectedAttributes) {
      this.personalizationAttribute = this.selectedAttributes[0];
    }
  }

  public setChildAttributes(): void {
    this.showChildAttributes = false;

    this.selectedChildAttributes = this.personalizationAttribute.childAttributes.map((attr: any) => new CRMAttribute(attr.Label, attr.Value, attr.ChildAttributes));

    if (this.selectedChildAttributes.length > 1) {
      this.selectedChildAttributes.sort(function (a, b) {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      });
      
      this.selectedChildAttributes.splice(0, 0, new CRMAttribute(this.noneText, '', null));
      
      this.childAttribute = this.selectedChildAttributes[0];
      
      this.showChildAttributes = true;
    }
  }

  private handleGetPersonalizationAttributesResult(res: any) {
    if (res) {
      if (res.Contacts) {
        this.contactAttributes = res.Contacts.map((contactAttr: any) => new CRMAttribute(contactAttr.Label, contactAttr.Value, contactAttr.ChildAttributes));
        this.contactAttributes.splice(0, 0, new CRMAttribute(this.noneText, '', null));
      }
      if (res.Leads) {
        this.leadAttributes = res.Leads.map((leadAttr: any) => new CRMAttribute(leadAttr.Label, leadAttr.Value, leadAttr.ChildAttributes));
        this.leadAttributes.splice(0, 0, new CRMAttribute(this.noneText, '', null));
      }
      if (res.Accounts) {
        this.accountAttributes = res.Accounts.map((accountAttr: any) => new CRMAttribute(accountAttr.Label, accountAttr.Value, accountAttr.ChildAttributes));
        this.accountAttributes.splice(0, 0, new CRMAttribute(this.noneText, '', null));
      }
      if (res.Senders) {
        this.ownerAttributes = res.Senders.map((ownerAttr: any) => new CRMAttribute(ownerAttr.Label, ownerAttr.Value, ownerAttr.ChildAttributes));
        this.ownerAttributes.splice(0, 0, new CRMAttribute(this.noneText, '', null));
      }
      if (res.MailSend) {
        this.emailSendAttributes = res.MailSend.map((emailSendAttr: any) => new CRMAttribute(emailSendAttr.Label, emailSendAttr.Value, emailSendAttr.ChildAttributes));
        this.emailSendAttributes.splice(0, 0, new CRMAttribute(this.noneText, '', null));
      }

      this.setPersonalizationAttributes();
    }
  }

  private handleGetPersonalizationAttributesError(error: any) {
    this.logger.error(`error in get personalization attributes from server: ${error.originalError || error}`);
  }

  private getPersonalizationAttributesFromServer() {
    this.getPersonalizationAttributes()
      .pipe(finalize(() => this.setLoading(false)))
      .subscribe((data) => this.handleGetPersonalizationAttributesResult(data),
        (err) => {
          this.handleGetPersonalizationAttributesError(err);
        });
  }

  public ngOnInit() {
    this.getPersonalizationAttributesFromServer();
  }
}
